import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CalendarModule } from 'primeng/calendar';
import { ChartModule } from 'primeng/chart';
import { TableModule } from 'primeng/table';
import { InMemoryCacheService, isGoogleAuthorizationError, MessageType, UtilsService } from 'src/app/common';
import { RouterModule } from '@angular/router';
import { OverviewBoxComponent } from 'src/app/components/overview-box/overview-box.component';
import { ChartWrapperComponent } from 'src/app/components/seo/chart-wrapper/chart-wrapper.component';
import { PositionDistributionChartComponent } from 'src/app/components/seo/position-distribution-chart/position-distribution-chart.component';
import { ReferringDomainRequest } from '../models/site-search-request';
import { SeoApiService } from '../seo-api.service';
import { mapDailyTrend, mapGeography, mapLabelValue, mapOverview, mapReferrals } from './google-analytics-report.mapper';
import { GaBatchResponse, GaChartPoint, GaGeographyRow, GaOverview, GaReferralRow, GaReportData, GaReportResponse } from './google-analytics-report.models';

@Component({
  selector: 'gig-google-analytics-traffic-report',
  standalone: true,
  imports: [CalendarModule, CommonModule, ChartModule, TableModule, FormsModule, RouterModule, OverviewBoxComponent, ChartWrapperComponent, PositionDistributionChartComponent],
  templateUrl: './google-analytics-traffic-report.component.html',
  styleUrl: './google-analytics-traffic-report.component.scss'
})
export class GoogleAnalyticsTrafficReportComponent implements OnInit {
  public generalKpiResponse: GaOverview = mapOverview(undefined);
  public trafficDoughnutChartData: GaChartPoint[] = [];
  public visitsByUserTypeChartData: GaChartPoint[] = [];
  public visitsByOverTimeChartData: GaChartPoint[] = [];
  public bounceRateOverTimeChartData: GaChartPoint[] = [];
  public top3ChannelsByConversionChartData: GaChartPoint[] = [];
  public top3CampaignsByConversionChartData: GaChartPoint[] = [];
  public top3PagesChartData: GaChartPoint[] = [];
  public dateRange: Date[] = [];
  public referralSources: GaReferralRow[] = [];
  public gaSummaryDataActiveUser: number = 0;
  public gaSummaryDataSessions: number = 0;
  public gaSummaryDataNewUser: number = 0;
  public gaSummaryTableData: GaGeographyRow[] = [];
  public isTrafficDataAvailable: boolean = false;
  public hasLoaded: boolean = false;
  public loadError: boolean = false;
  public authorizationRequired: boolean = false;
  public readonly chartColors1: string[] = ['#42A5F5', '#66BB6A', '#FFA726', '#FF7043', '#AB47BC'];
  public readonly chartColorsDoughnut: Array<{ hex: string }> = [
    { hex: '#FF6384' }, { hex: '#36A2EB' }, { hex: '#FFCE56' },
    { hex: '#4BC0C0' }, { hex: '#9966FF' }, { hex: '#FF9F40' }
  ];

  private loadSequence: number = 0;

  public constructor(
    private readonly cache: InMemoryCacheService,
    private readonly utils: UtilsService,
    private readonly seoApiService: SeoApiService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this.dateRange = this.getLast30Days();
    if (!this.isGoogleAnalyticsConnected()) {
      this.hasLoaded = true;
      const google = this.cache?.selectedProject?.externalProviders?.google;
      this.authorizationRequired = !!google?.propertyId && !(google.accessToken || google.refreshToken);
      if (!this.authorizationRequired) {
        this.utils.errorMessage(MessageType.TOAST, 'Google Analytics Not Connected', 'Please connect Google Analytics in settings to view data.');
      }
      return;
    }
    void this.loadReport();
  }

  public onDateRangeChange(_event?: unknown): void {
    if (!this.hasCompleteDateRange() || !this.isGoogleAnalyticsConnected()) {
      return;
    }
    void this.loadReport();
  }

  public onExportToExcel(): void {
    const headers = {
      referralSource: 'Referral Source', visits: 'Visits', visitsPercentage: 'Share of referral sessions (%)',
      newVisits: 'New Users', pageviewsPerVisit: 'Page Views Per Session', bounceRate: 'Bounce Rate'
    };
    this.utils.exportCSVFile(headers, this.referralSources.map(({ rank, backlinks, ...row }) => row), 'Traffic_Summary_By_Referral_Source');
  }

  private async loadReport(): Promise<void> {
    if (!this.hasCompleteDateRange()) {
      return;
    }
    const sequence = ++this.loadSequence;
    this.cache.loading = true;
    this.loadError = false;
    try {
      const range = this.getApiDateRange();
      const reportOptions = [
        { metrics: ['sessions', 'activeUsers', 'newUsers', 'averageSessionDuration', 'screenPageViewsPerSession', 'bounceRate', 'screenPageViews', 'keyEvents'], range },
        { dimensions: ['date'], metrics: ['sessions', 'bounceRate'], range },
        { dimensions: ['sessionSourceMedium'], metrics: ['sessions', 'newUsers', 'screenPageViews', 'engagedSessions'], range, limit: 100, filter: this.referralFilter() },
        { dimensions: ['newVsReturning'], metrics: ['totalUsers'], range },
        { dimensions: ['sessionDefaultChannelGrouping'], metrics: ['keyEvents'], range, limit: 3, orderBy: 'keyEvents' },
        { dimensions: ['sessionCampaignName', 'sessionDefaultChannelGrouping'], metrics: ['keyEvents'], range, limit: 3, orderBy: 'keyEvents' },
        { dimensions: ['pagePath'], metrics: ['keyEvents'], range, limit: 3, orderBy: 'keyEvents' },
        { dimensions: ['country', 'city'], metrics: ['sessions', 'activeUsers', 'newUsers'], range, limit: 1000 }
      ];
      const [firstBatch, secondBatch] = await Promise.all([
        this.fetchBatch(reportOptions.slice(0, 5)),
        this.fetchBatch(reportOptions.slice(5))
      ]);
      const [overview, trend, referrals, userTypes, channels, campaigns, pages, geography] = [...firstBatch, ...secondBatch];
      if (sequence !== this.loadSequence) {
        return;
      }
      this.applySnapshot(overview, trend, referrals, userTypes, channels, campaigns, pages, geography);
      void this.enrichReferrals(sequence);
    } catch (error) {
      if (sequence === this.loadSequence) {
        this.authorizationRequired = isGoogleAuthorizationError(error);
        this.loadError = !this.authorizationRequired;
        this.isTrafficDataAvailable = false;
        if (this.authorizationRequired) {
          this.clearCachedGoogleAuthorization();
        } else {
          this.utils.handleApiError(error, 'Failed to fetch Google Analytics report');
        }
      }
    } finally {
      if (sequence === this.loadSequence) {
        this.hasLoaded = true;
        this.cache.loading = false;
        this.cdr.markForCheck();
      }
    }
  }

  private applySnapshot(
    overview: GaReportResponse, trend: GaReportResponse, referrals: GaReportResponse, userTypes: GaReportResponse,
    channels: GaReportResponse, campaigns: GaReportResponse, pages: GaReportResponse, geography: GaReportResponse
  ): void {
    this.generalKpiResponse = mapOverview(overview);
    this.gaSummaryDataSessions = this.generalKpiResponse.visits;
    this.gaSummaryDataActiveUser = this.generalKpiResponse.activeUsers;
    this.gaSummaryDataNewUser = this.generalKpiResponse.newUsers;
    this.referralSources = mapReferrals(referrals);
    this.trafficDoughnutChartData = this.referralSources.map((row) => ({ label: row.referralSource, value: Number(row.visitsPercentage) }));
    this.visitsByUserTypeChartData = (userTypes.rows ?? []).map((row) => ({ userType: row.dimensionValues?.[0]?.value, sessions: Number(row.metricValues?.[0]?.value ?? 0) }));
    const daily = mapDailyTrend(trend);
    this.visitsByOverTimeChartData = daily.sessions;
    this.bounceRateOverTimeChartData = daily.bounceRate;
    this.top3ChannelsByConversionChartData = mapLabelValue(channels);
    this.top3CampaignsByConversionChartData = mapLabelValue(campaigns, true);
    this.top3PagesChartData = mapLabelValue(pages);
    this.gaSummaryTableData = mapGeography(geography);
    this.isTrafficDataAvailable = this.generalKpiResponse.visits > 0 || this.referralSources.length > 0;
  }

  private buildReport(options: {
    dimensions?: string[]; metrics: string[]; range: { startDate: string; endDate: string };
    limit?: number; orderBy?: string; filter?: unknown;
  }): GaReportData {
    const reportData: GaReportData = {
      dimensions: options.dimensions?.map((name) => ({ name })),
      metrics: options.metrics.map((name) => ({ name })),
      dateRanges: [options.range]
    };
    if (options.limit) reportData.limit = options.limit;
    if (options.orderBy) reportData.orderBys = [{ metric: { metricName: options.orderBy }, desc: true }];
    if (options.filter) reportData.dimensionFilter = options.filter;
    return reportData;
  }

  private async fetchBatch(options: Array<{
    dimensions?: string[]; metrics: string[]; range: { startDate: string; endDate: string };
    limit?: number; orderBy?: string; filter?: unknown;
  }>): Promise<GaReportResponse[]> {
    const result = await this.cache.getGoogleAnalyticsBatchData({
      propertyId: this.cache?.selectedProject?.externalProviders?.google?.propertyId,
      projectId: this.cache?.selectedProject?.id,
      reports: options.map((item) => this.buildReport(item))
    }) as { data: GaBatchResponse | null; error: unknown | null };
    if (result.error) throw result.error;
    return result.data?.reports ?? [];
  }

  private async enrichReferrals(sequence: number): Promise<void> {
    const sources = this.referralSources.slice(0, 20);
    if (!sources.length) return;
    const conditions = sources.map((item) => ['domain', '=', item.referralSource]);
    const filterParts: unknown[] = [];
    conditions.forEach((condition, index) => {
      if (index) filterParts.push('or');
      filterParts.push(condition);
    });
    const payload: ReferringDomainRequest = {
      target: this.utils.stripHost(this.cache?.selectedProject?.domain),
      limit: Math.min(this.cache?.seoLimits?.siteSearchServiceReferringDomain ?? 20, 20),
      orderByString: JSON.stringify(['rank,desc']),
      excludeInternalBacklinks: true,
      backlinksFiltersString: '',
      filtersString: JSON.stringify(filterParts)
    };
    const { data, error } = await this.seoApiService.getBacklinksReferringDomains(payload);
    if (sequence !== this.loadSequence || error || !Array.isArray(data?.items)) return;
    const authority = new Map<string, { rank?: number; backlinks?: number }>(data.items.map((item: { domain: string; rank?: number; backlinks?: number }) => [item.domain, item]));
    this.referralSources = this.referralSources.map((source) => ({ ...source, ...authority.get(source.referralSource) }));
    this.cdr.markForCheck();
  }

  private referralFilter(): unknown {
    return { filter: { fieldName: 'sessionDefaultChannelGrouping', stringFilter: { matchType: 'EXACT', value: 'Referral' } } };
  }

  private isGoogleAnalyticsConnected(): boolean {
    const google = this.cache?.selectedProject?.externalProviders?.google;
    return !!google?.propertyId && !!(google.accessToken || google.refreshToken);
  }

  private clearCachedGoogleAuthorization(): void {
    const google = this.cache?.selectedProject?.externalProviders?.google;
    if (!google) {
      return;
    }

    google.accessToken = '';
    google.refreshToken = '';
  }

  private hasCompleteDateRange(): boolean {
    return this.dateRange?.length === 2 && !!this.dateRange[0] && !!this.dateRange[1];
  }

  private getApiDateRange(): { startDate: string; endDate: string } {
    return { startDate: this.formatLocalDate(this.dateRange[0]), endDate: this.formatLocalDate(this.dateRange[1]) };
  }

  private formatLocalDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getLast30Days(): Date[] {
    const endDate = new Date();
    const startDate = new Date(endDate);
    startDate.setDate(endDate.getDate() - 30);
    return [startDate, endDate];
  }
}
